import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import type {
  ComplianceRangeKey,
  DrilldownTarget,
  ExecutionsHistoryQuery,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';
import type { SectionStatus } from '@src/packages/dashboard/components/ReportSummary';
import { getHistorySectionErrorMessageId } from '@src/packages/dashboard/components/ReportSummary/historySectionErrorMessageIds';
import { useDebouncedValue } from '@src/packages/utils';

import { isUserDrilldown } from '../selectors';
import type { TaskDrilldown } from '../types';
import { useHistoryDrilldown } from './useHistoryDrilldown';
import { useTasksDrilldownColumns } from './useTasksDrilldownColumns';

export const DRILLDOWN_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const getSummaryMessageId = (drilldown: TaskDrilldown) => {
  if (isUserDrilldown(drilldown)) return 'dashboards.tasks.summary.drilldown.userSummary';
  if (drilldown.kind === 'taskRange') return 'dashboards.tasks.summary.taskCompliance.drawerSummary';
  return 'dashboards.tasks.summary.drilldown.orderSummary';
};

const getTag = (drilldown: TaskDrilldown) => {
  if (drilldown.kind === 'taskRange') return drilldown.range.label;
  if (drilldown.kind === 'userRange') return drilldown.label;
  return undefined;
};

/** Backend target of an opened drill-down. */
const toTarget = (drilldown: TaskDrilldown): DrilldownTarget => {
  switch (drilldown.kind) {
    case 'taskRange':
      return { kind: 'taskRange', filter: drilldown.range.key as ComplianceRangeKey };
    case 'userRange':
      return { kind: 'userRange', filter: drilldown.range.key as ComplianceRangeKey };
    default:
      return { kind: drilldown.kind, filter: drilldown.filter } as DrilldownTarget;
  }
};

/** Drill-down drawer: the opened card or range, its server-side search and pagination. */
export const useTasksDrilldown = (query: ExecutionsHistoryQuery) => {
  const intl = useIntl();
  const { orderColumns, userColumns } = useTasksDrilldownColumns();
  const [drilldown, setDrilldown] = useState<TaskDrilldown | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const params = useMemo(
    () => (drilldown
      ? { drilldown: toTarget(drilldown), page, pageSize: DRILLDOWN_PAGE_SIZE, search: debouncedSearch.trim() }
      : null),
    [debouncedSearch, drilldown, page],
  );
  const { loading, result, dataResetKey, retry } = useHistoryDrilldown(query, params);

  const open = useCallback((next: TaskDrilldown) => {
    setSearch('');
    setPage(1);
    setDrilldown(next);
  }, []);
  const close = useCallback(() => setDrilldown(null), []);
  const changeSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const data = result?.status === 'ok' ? result.response.data : undefined;
  const total = data?.total ?? 0;
  const orderRows = useMemo(
    () => (data?.kind === 'orders' ? data.rows.map(row => ({ ...row, id: row.executionId })) : []),
    [data],
  );
  const userRows = useMemo(
    () => (data?.kind === 'users' ? data.rows.map(row => ({ ...row, id: row.userId })) : []),
    [data],
  );

  const table = useMemo(() => {
    if (!drilldown) return null;
    if (isUserDrilldown(drilldown)) {
      return {
        kind: 'users' as const,
        columns: userColumns,
        dataSource: userRows,
        dataResetKey,
      };
    }
    return {
      kind: 'orders' as const,
      columns: orderColumns,
      dataSource: orderRows,
      dataResetKey,
    };
  }, [dataResetKey, drilldown, orderColumns, orderRows, userColumns, userRows]);

  const status: SectionStatus = result?.status === 'error' && !loading
    ? {
      kind: 'error',
      message: intl.formatMessage({ id: getHistorySectionErrorMessageId(result.errorCode) }),
      retryLabel: intl.formatMessage({ id: 'dashboards.tasks.summary.section.retry' }),
      onRetry: retry,
    }
    : { kind: 'ready' };

  const drawer = {
    open: drilldown !== null,
    title: drilldown?.title ?? '',
    summary: drilldown ? intl.formatMessage({ id: getSummaryMessageId(drilldown) }, { count: total }) : '',
    tag: drilldown ? getTag(drilldown) : undefined,
    search: {
      value: search,
      placeholder: intl.formatMessage({
        id: drilldown && isUserDrilldown(drilldown)
          ? 'dashboards.tasks.summary.drilldown.userSearch'
          : 'dashboards.tasks.summary.taskCompliance.search',
      }),
      onChange: changeSearch,
    },
    onClose: close,
  };

  const pagination = {
    current: page,
    pageSize: DRILLDOWN_PAGE_SIZE,
    total,
    onChange: setPage,
  };

  return { open, drawer, table, loading, status, pagination };
};
